
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

export default function Home() {
  const { t, i18n } = useTranslation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [selectedService, setSelectedService] = useState<string | null>(null);

  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng);
  };

  const services = [
    {
      id: 'consultoria',
      title: t('services.consultoria.title'),
      description: t('services.consultoria.description'),
      icon: 'ri-user-settings-line',
      features: t('services.consultoria.features', { returnObjects: true }) as string[]
    },
    {
      id: 'marketing',
      title: t('services.marketing.title'),
      description: t('services.marketing.description'),
      icon: 'ri-megaphone-line',
      features: t('services.marketing.features', { returnObjects: true }) as string[]
    },
    {
      id: 'eventos',
      title: t('services.eventos.title'),
      description: t('services.eventos.description'),
      icon: 'ri-calendar-event-line',
      features: t('services.eventos.features', { returnObjects: true }) as string[]
    },
    {
      id: 'publicidad',
      title: t('services.publicidad.title'),
      description: t('services.publicidad.description'),
      icon: 'ri-advertisement-line',
      features: t('services.publicidad.features', { returnObjects: true }) as string[]
    },
    {
      id: 'redes',
      title: t('services.redes.title'),
      description: t('services.redes.description'),
      icon: 'ri-share-line',
      features: t('services.redes.features', { returnObjects: true }) as string[]
    },
    {
      id: 'desarrollo',
      title: t('services.desarrollo.title'),
      description: t('services.desarrollo.description'),
      icon: 'ri-code-line',
      features: t('services.desarrollo.features', { returnObjects: true }) as string[]
    },
    {
      id: 'web3',
      title: t('services.web3.title'),
      description: t('services.web3.description'),
      icon: 'ri-links-line',
      features: t('services.web3.features', { returnObjects: true }) as string[]
    },
    {
      id: 'ecommerce',
      title: t('services.ecommerce.title'),
      description: t('services.ecommerce.description'),
      icon: 'ri-shopping-cart-line',
      features: t('services.ecommerce.features', { returnObjects: true }) as string[]
    }
  ];

  const stats = [
    { number: t('stats.projects'), label: t('stats.projectsLabel') },
    { number: t('stats.commitment'), label: t('stats.commitmentLabel') },
    { number: t('stats.experience'), label: t('stats.experienceLabel') },
    { number: t('stats.support'), label: t('stats.supportLabel') }
  ];

  const benefits = [
    {
      icon: 'ri-rocket-line',
      title: t('benefits.innovation.title'),
      description: t('benefits.innovation.description')
    },
    {
      icon: 'ri-team-line',
      title: t('benefits.team.title'),
      description: t('benefits.team.description')
    },
    {
      icon: 'ri-shield-check-line',
      title: t('benefits.quality.title'),
      description: t('benefits.quality.description')
    },
    {
      icon: 'ri-customer-service-line',
      title: t('benefits.attention.title'),
      description: t('benefits.attention.description')
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="bg-black shadow-xl sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <div className="flex items-center space-x-2">
                <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg flex items-center justify-center">
                  <i className="ri-global-line text-white text-xl"></i>
                </div>
                <h1 className="text-2xl font-bold bg-gradient-to-r from-orange-500 to-green-500 bg-clip-text text-transparent" style={{ fontFamily: '"Inter", sans-serif' }}>
                  GlobalTechWay
                </h1>
              </div>
            </div>
            
            <div className="hidden md:block">
              <div className="ml-10 flex items-baseline space-x-8">
                <a href="#inicio" className="text-white hover:text-orange-500 px-3 py-2 text-sm font-medium cursor-pointer whitespace-nowrap transition-colors">{t('navigation.home')}</a>
                <a href="#servicios" className="text-gray-300 hover:text-orange-500 px-3 py-2 text-sm font-medium cursor-pointer whitespace-nowrap transition-colors">{t('navigation.services')}</a>
                <a href="#nosotros" className="text-gray-300 hover:text-orange-500 px-3 py-2 text-sm font-medium cursor-pointer whitespace-nowrap transition-colors">{t('navigation.about')}</a>
                <a href="#beneficios" className="text-gray-300 hover:text-orange-500 px-3 py-2 text-sm font-medium cursor-pointer whitespace-nowrap transition-colors">{t('navigation.benefits')}</a>
                <a href="#contacto" className="bg-gradient-to-r from-orange-500 to-orange-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:from-orange-600 hover:to-orange-700 cursor-pointer whitespace-nowrap transition-all">{t('navigation.contact')}</a>
                
                {/* Language Switcher */}
                <button
                  onClick={() => changeLanguage(i18n.language === 'en' ? 'es' : 'en')}
                  className="text-gray-300 hover:text-orange-500 px-3 py-2 text-sm font-medium cursor-pointer whitespace-nowrap transition-colors flex items-center"
                >
                  <i className="ri-global-line mr-1"></i>
                  {t('common.language')}
                </button>
              </div>
            </div>

            <div className="md:hidden">
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="text-white hover:text-orange-500 p-2 cursor-pointer transition-colors"
              >
                <i className={`ri-${isMenuOpen ? 'close' : 'menu'}-line text-xl`}></i>
              </button>
            </div>
          </div>

          {isMenuOpen && (
            <div className="md:hidden">
              <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3 bg-black border-t border-gray-800">
                <a href="#inicio" className="text-white hover:text-orange-500 block px-3 py-2 text-base font-medium cursor-pointer transition-colors">{t('navigation.home')}</a>
                <a href="#servicios" className="text-gray-300 hover:text-orange-500 block px-3 py-2 text-base font-medium cursor-pointer transition-colors">{t('navigation.services')}</a>
                <a href="#nosotros" className="text-gray-300 hover:text-orange-500 block px-3 py-2 text-base font-medium cursor-pointer transition-colors">{t('navigation.about')}</a>
                <a href="#beneficios" className="text-gray-300 hover:text-orange-500 block px-3 py-2 text-base font-medium cursor-pointer transition-colors">{t('navigation.benefits')}</a>
                <a href="#contacto" className="text-gray-300 hover:text-orange-500 block px-3 py-2 text-base font-medium cursor-pointer transition-colors">{t('navigation.contact')}</a>
                <button
                  onClick={() => changeLanguage(i18n.language === 'en' ? 'es' : 'en')}
                  className="text-gray-300 hover:text-orange-500 block px-3 py-2 text-base font-medium cursor-pointer transition-colors w-full text-left"
                >
                  <i className="ri-global-line mr-2"></i>
                  {t('common.language')}
                </button>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section id="inicio" className="relative min-h-screen flex items-center justify-center bg-cover bg-center bg-no-repeat" 
               style={{backgroundImage: `url('https://public.readdy.ai/ai/img_res/5e338e705b9dc7bf3fac20c50a99c17c.jpg')`}}>
        <div className="absolute inset-0 bg-black/80"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="w-full">
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
              {t('hero.title')} <span className="bg-gradient-to-r from-orange-500 to-green-500 bg-clip-text text-transparent">{t('hero.titleHighlight')}</span> {t('hero.titleEnd')}
            </h1>
            <p className="text-xl md:text-2xl text-gray-200 mb-8 max-w-3xl mx-auto">
              {t('hero.subtitle')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button className="bg-gradient-to-r from-orange-500 to-orange-600 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:from-orange-600 hover:to-orange-700 transition-all duration-300 cursor-pointer whitespace-nowrap">
                {t('hero.ctaPrimary')}
              </button>
              <button className="border-2 border-green-500 text-green-500 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-green-500 hover:text-white transition-all duration-300 cursor-pointer whitespace-nowrap">
                {t('hero.ctaSecondary')}
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-orange-500 to-green-500 bg-clip-text text-transparent mb-2">{stat.number}</div>
                <div className="text-gray-300 font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="servicios" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-black mb-4">{t('services.title')}</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t('services.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8" data-product-shop>
            {services.map((service) => (
              <div 
                key={service.id}
                className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-200 cursor-pointer group hover:border-orange-500"
                onClick={() => setSelectedService(selectedService === service.id ? null : service.id)}
              >
                <div className="w-16 h-16 bg-gradient-to-br from-orange-100 to-green-100 rounded-lg flex items-center justify-center mb-4 group-hover:from-orange-500 group-hover:to-green-500 transition-all duration-300">
                  <i className={`${service.icon} text-2xl text-orange-600 group-hover:text-white transition-colors duration-300`}></i>
                </div>
                <h3 className="text-xl font-semibold text-black mb-3">{service.title}</h3>
                <p className="text-gray-600 mb-4">{service.description}</p>
                
                {selectedService === service.id && (
                  <div className="border-t border-gray-200 pt-4 mt-4">
                    <h4 className="font-semibold text-black mb-2">{t('services.includes')}</h4>
                    <ul className="space-y-1">
                      {service.features.map((feature, idx) => (
                        <li key={idx} className="text-sm text-gray-600 flex items-center">
                          <i className="ri-check-line text-green-500 mr-2"></i>
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                
                <button className="mt-4 text-orange-600 font-semibold hover:text-orange-700 cursor-pointer whitespace-nowrap transition-colors">
                  {selectedService === service.id ? t('services.hideDetails') : t('services.viewMore')}
                  <i className={`ri-arrow-${selectedService === service.id ? 'up' : 'down'}-s-line ml-1`}></i>
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="nosotros" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-black mb-6">{t('about.title')}</h2>
              <p className="text-lg text-gray-600 mb-6">
                {t('about.description')}
              </p>
              
              <div className="space-y-4">
                <div className="flex items-start">
                  <div className="w-6 h-6 bg-gradient-to-r from-orange-500 to-green-500 rounded-full flex items-center justify-center mt-1 mr-4">
                    <i className="ri-check-line text-white text-sm"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-black">{t('about.experiencedTeam')}</h3>
                    <p className="text-gray-600">{t('about.experiencedTeamDesc')}</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="w-6 h-6 bg-gradient-to-r from-orange-500 to-green-500 rounded-full flex items-center justify-center mt-1 mr-4">
                    <i className="ri-check-line text-white text-sm"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-black">{t('about.cuttingEdgeTech')}</h3>
                    <p className="text-gray-600">{t('about.cuttingEdgeTechDesc')}</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="w-6 h-6 bg-gradient-to-r from-orange-500 to-green-500 rounded-full flex items-center justify-center mt-1 mr-4">
                    <i className="ri-check-line text-white text-sm"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-black">{t('about.personalizedApproach')}</h3>
                    <p className="text-gray-600">{t('about.personalizedApproachDesc')}</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="relative">
              <img 
                src="https://readdy.ai/api/search-image?query=Professional%20business%20team%20working%20together%20in%20modern%20office%20environment%2C%20diverse%20group%20of%20technology%20consultants%20collaborating%20on%20digital%20projects%2C%20clean%20corporate%20setting%20with%20laptops%20and%20tablets%2C%20teamwork%20and%20innovation%20concept%2C%20bright%20natural%20lighting%2C%20contemporary%20workspace%20design%20with%20orange%20and%20green%20accent%20elements&width=600&height=400&seq=team2&orientation=landscape"
                alt="Equipo GlobalTechWay"
                className="rounded-xl shadow-lg object-cover w-full h-96"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section id="beneficios" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-black mb-4">{t('benefits.title')}</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t('benefits.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <div key={index} className="text-center p-6 bg-gray-50 rounded-xl hover:bg-white hover:shadow-lg transition-all duration-300 border border-gray-100 hover:border-orange-300">
                <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className={`${benefit.icon} text-2xl text-white`}></i>
                </div>
                <h3 className="text-xl font-semibold text-black mb-3">{benefit.title}</h3>
                <p className="text-gray-600">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-black via-gray-900 to-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            {t('cta.title')}
          </h2>
          <p className="text-xl text-gray-200 mb-8 max-w-2xl mx-auto">
            {t('cta.subtitle')}
          </p>
          <button className="bg-gradient-to-r from-orange-500 to-green-500 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:from-orange-600 hover:to-green-600 transition-all duration-300 cursor-pointer whitespace-nowrap">
            {t('cta.button')}
          </button>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contacto" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-black mb-4">{t('contact.title')}</h2>
            <p className="text-xl text-gray-600">
              {t('contact.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <div className="space-y-8">
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-green-500 rounded-lg flex items-center justify-center mr-4">
                    <i className="ri-mail-line text-white text-xl"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-black mb-1">{t('contact.email')}</h3>
                    <p className="text-gray-600">info@globaltechway.com</p>
                    <p className="text-gray-600">.</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-green-500 rounded-lg flex items-center justify-center mr-4">
                    <i className="ri-phone-line text-white text-xl"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-black mb-1">{t('contact.phone')}</h3>
                    <p className="text-gray-600">+1 (786) 822-6181</p>
                    <p className="text-gray-600">-</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-green-500 rounded-lg flex items-center justify-center mr-4">
                    <i className="ri-time-line text-white text-xl"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-black mb-1">{t('contact.schedule')}</h3>
                    <p className="text-gray-600">{t('contact.scheduleWeekdays')}</p>
                    <p className="text-gray-600">{t('contact.scheduleWeekend')}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-lg border border-gray-200">
              <form id="contact-form" action="https://readdy.ai/api/form/submit?id=6776ae6c9e94e46b74bc9f4c" method="POST" data-readdy-form>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-black mb-2">
                      {t('contact.form.nameRequired')}
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                      placeholder={t('contact.form.namePlaceholder')}
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-black mb-2">
                      {t('contact.form.emailRequired')}
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                      placeholder={t('contact.form.emailPlaceholder')}
                    />
                  </div>
                </div>

                <div className="mb-6">
                  <label htmlFor="company" className="block text-sm font-medium text-black mb-2">
                    {t('contact.form.company')}
                  </label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                    placeholder={t('contact.form.companyPlaceholder')}
                  />
                </div>

                <div className="mb-6">
                  <label htmlFor="service" className="block text-sm font-medium text-black mb-2">
                    {t('contact.form.serviceRequired')}
                  </label>
                  <select
                    id="service"
                    name="service"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm pr-8"
                  >
                    <option value="">{t('contact.form.servicePlaceholder')}</option>
                    <option value="consultoria">{t('contact.form.services.consultoria')}</option>
                    <option value="marketing">{t('contact.form.services.marketing')}</option>
                    <option value="eventos">{t('contact.form.services.eventos')}</option>
                    <option value="publicidad">{t('contact.form.services.publicidad')}</option>
                    <option value="redes">{t('contact.form.services.redes')}</option>
                    <option value="desarrollo">{t('contact.form.services.desarrollo')}</option>
                    <option value="web3">{t('contact.form.services.web3')}</option>
                    <option value="ecommerce">{t('contact.form.services.ecommerce')}</option>
                  </select>
                </div>

                <div className="mb-6">
                  <label htmlFor="message" className="block text-sm font-medium text-black mb-2">
                    {t('contact.form.messageRequired')}
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    rows={5}
                    maxLength={500}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm resize-vertical"
                    placeholder={t('contact.form.messagePlaceholder')}
                  ></textarea>
                  <p className="text-xs text-gray-500 mt-1">{t('contact.form.maxChars')}</p>
                </div>

                <button
                  type="submit"
                  className="w-full bg-gradient-to-r from-orange-500 to-green-500 text-white py-4 px-6 rounded-lg font-semibold hover:from-orange-600 hover:to-green-600 transition-all duration-300 cursor-pointer whitespace-nowrap"
                >
                  {t('contact.form.submit')}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="col-span-1 md:col-span-2">
              <div className="flex items-center space-x-2 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg flex items-center justify-center">
                  <i className="ri-global-line text-white text-xl"></i>
                </div>
                <h3 className="text-2xl font-bold bg-gradient-to-r from-orange-500 to-green-500 bg-clip-text text-transparent" style={{ fontFamily: '"Inter", sans-serif' }}>
                  GlobalTechWay
                </h3>
              </div>
              <p className="text-gray-300 mb-4 max-w-md">
                {t('footer.description')}
              </p>
            </div>

            <div>
              <h4 className="text-lg font-semibold mb-4 text-orange-500">{t('footer.services')}</h4>
              <ul className="space-y-2">
                <li><a href="#servicios" className="text-gray-300 hover:text-green-500 cursor-pointer transition-colors">{t('footer.consulting')}</a></li>
                <li><a href="#servicios" className="text-gray-300 hover:text-green-500 cursor-pointer transition-colors">{t('footer.digitalMarketing')}</a></li>
                <li><a href="#servicios" className="text-gray-300 hover:text-green-500 cursor-pointer transition-colors">{t('footer.webDevelopment')}</a></li>
                <li><a href="#servicios" className="text-gray-300 hover:text-green-500 cursor-pointer transition-colors">{t('footer.ecommerce')}</a></li>
                <li><a href="#servicios" className="text-gray-300 hover:text-green-500 cursor-pointer transition-colors">{t('footer.web3')}</a></li>
              </ul>
            </div>

            <div>
              <h4 className="text-lg font-semibold mb-4 text-orange-500">{t('footer.quickLinks')}</h4>
              <ul className="space-y-2">
                <li><a href="#inicio" className="text-gray-300 hover:text-green-500 cursor-pointer transition-colors">{t('footer.home')}</a></li>
                <li><a href="#nosotros" className="text-gray-300 hover:text-green-500 cursor-pointer transition-colors">{t('footer.about')}</a></li>
                <li><a href="#beneficios" className="text-gray-300 hover:text-green-500 cursor-pointer transition-colors">{t('footer.whyUs')}</a></li>
                <li><a href="#contacto" className="text-gray-300 hover:text-green-500 cursor-pointer transition-colors">{t('footer.contact')}</a></li>
                <li><a href="https://readdy.ai/?origin=logo" target="_blank" rel="noopener noreferrer" className="text-gray-300 hover:text-green-500 cursor-pointer transition-colors">{t('footer.madeWith')}</a></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 mt-8 pt-8 text-center">
            <p className="text-gray-300">
              {t('footer.copyright')}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
